/**
 * MapSidePanel - Side panel with project/status filters and device list.
 */

import { Select, Radio, Button, Spin, Typography } from 'antd';
import { MaterialIcon } from '@/components/MaterialIcon';
import { Project, View } from '@/types';

const { Text } = Typography;

export type StatusFilter = 'all' | 'online' | 'offline';

interface MapSidePanelProps {
  projects: Project[];
  views: View[];
  selectedProjectId: string | null;
  statusFilter: StatusFilter;
  onProjectChange: (projectId: string | null) => void;
  onStatusFilterChange: (filter: StatusFilter) => void;
  onViewClick: (view: View) => void;
  onRefreshAll: () => void;
  isRefreshing: boolean;
  getConnectionStatus: (view: View) => 'online' | 'offline' | 'survival' | 'unpaired' | 'never_connected';
  loadingViewIds: Set<string>;
  collapsed: boolean;
  onToggle: () => void;
}

export function MapSidePanel({
  projects,
  views,
  selectedProjectId,
  statusFilter,
  onProjectChange,
  onStatusFilterChange,
  onViewClick,
  onRefreshAll,
  isRefreshing,
  getConnectionStatus: getStatus,
  loadingViewIds,
  collapsed,
  onToggle,
}: MapSidePanelProps) {
  // Collapsed: show only a narrow strip with expand arrow
  if (collapsed) {
    return (
      <div
        style={{
          width: 28,
          borderRight: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: 12,
          background: '#fff',
          cursor: 'pointer',
          flexShrink: 0,
        }}
        onClick={onToggle}
        title="Show panel"
      >
        <MaterialIcon name="chevron_right" size={20} />
      </div>
    );
  }

  return (
    <div
      style={{
        width: 280,
        borderRight: '1px solid #f0f0f0',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#fff',
        flexShrink: 0,
      }}
    >
      {/* Collapse arrow */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '8px 8px 0',
        }}
      >
        <div
          onClick={onToggle}
          style={{ cursor: 'pointer', lineHeight: 0 }}
          title="Hide panel"
        >
          <MaterialIcon name="chevron_left" size={20} />
        </div>
      </div>

      {/* Filters */}
      <div style={{ padding: '8px 12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
            Project
          </Text>
          <Select
            style={{ width: '100%' }}
            placeholder="All projects"
            allowClear
            value={selectedProjectId}
            onChange={(value) => onProjectChange(value ?? null)}
            options={projects.map((p) => ({ label: p.name, value: p.projectId }))}
            size="small"
          />
        </div>

        <div>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
            Status
          </Text>
          <Radio.Group
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            size="small"
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="all">All</Radio.Button>
            <Radio.Button value="online">Online</Radio.Button>
            <Radio.Button value="offline">Offline</Radio.Button>
          </Radio.Group>
        </div>
      </div>

      {/* Device list */}
      <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
        {views.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>
            No devices match filters
          </div>
        ) : (
          views.map((view) => {
            const status = getStatus(view);
            const isOnline = status === 'online';
            const hasLocation = !!view.gpsLocation;
            const isLoading = loadingViewIds.has(view.viewId);

            return (
              <div
                key={view.viewId}
                onClick={() => onViewClick(view)}
                style={{
                  padding: '8px 12px',
                  cursor: hasLocation ? 'pointer' : 'default',
                  borderBottom: '1px solid #fafafa',
                  opacity: hasLocation ? 1 : 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
                onMouseEnter={(e) => {
                  if (hasLocation) e.currentTarget.style.background = '#fafafa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {isLoading ? (
                  <Spin size="small" />
                ) : (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: !hasLocation
                        ? '#d9d9d9'
                        : isOnline
                          ? '#52c41a'
                          : '#ff4d4f',
                      flexShrink: 0,
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {view.name}
                  </div>
                  {!hasLocation && (
                    <div style={{ fontSize: 11, color: '#999' }}>No position</div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Refresh all button */}
      <div style={{ padding: 12, borderTop: '1px solid #f0f0f0' }}>
        <Button
          type="primary"
          icon={<MaterialIcon name="refresh" size={16} />}
          onClick={onRefreshAll}
          loading={isRefreshing}
          block
          size="small"
        >
          Refresh All
        </Button>
      </div>
    </div>
  );
}
